import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useUpdateInvite, useUpsertTranslation } from '../api/hooks/useInvites';
import { useToast } from './ToastContext';
import { PLACEHOLDER_IMAGES, resolvePlaceholderImages } from '../utils/placeholderImages';
import { formatEventDate } from '../utils/dateFormat';
import type { DateFormat } from '../utils/dateFormat';
import type {
  Invite,
  InviteType,
  EventType,
  InviteContent,
  InviteTheme,
  InviteSectionsEnabled,
  SectionTagRules,
  InviteImage,
  InviteLocationItem,
  InviteTranslatableContent,
  InviteLabelOverrides,
} from '../types';
import { SUPPORTED_LOCALES } from '../types';

// Sections whose content can be translated
const TRANSLATABLE_SECTIONS = new Set(['hero', 'details', 'schedule', 'rsvp', 'faq', 'gifts', 'nearby', 'ourStory']);

// Within those sections, which specific fields are text (translatable)
export const TRANSLATABLE_FIELDS: Record<string, Set<string>> = {
  hero:     new Set(['title', 'subtitle', 'preamble', 'dateDisplay']),
  details:  new Set(['title', 'labelText', 'dressCode', 'additionalInfo', 'locations']),
  rsvp:     new Set(['title', 'labelText', 'customMessage', 'customQuestions']),
  gifts:    new Set(['title', 'description', 'registryLabel']),
  gallery:  new Set(['title', 'labelText']),
  ourStory: new Set(['title', 'labelText']),
  schedule: new Set(['title', 'labelText']),
  faq:      new Set(['title', 'labelText']),
  nearby:   new Set(['title', 'labelText']),
  // schedule / faq / nearby items (non-title) are updated via updateContent (whole section)
};

export function mergeDraftIntoContent(base: InviteContent, draft: InviteTranslatableContent): InviteContent {
  return {
    ...base,
    hero:          draft.hero          ? { ...base.hero,    ...draft.hero }    : base.hero,
    details:       draft.details       ? { ...base.details, ...draft.details } : base.details,
    schedule:      draft.schedule      ? { ...base.schedule, ...draft.schedule } : base.schedule,
    rsvp:          draft.rsvp          ? { ...base.rsvp,    ...draft.rsvp }    : base.rsvp,
    faq:           draft.faq           ? { ...base.faq,     ...draft.faq }     : base.faq,
    gifts:         draft.gifts         ? { ...base.gifts,   ...draft.gifts }   : base.gifts,
    nearby:        draft.nearby        ? { ...(base.nearby ?? { items: [] }), ...draft.nearby } : base.nearby,
    gallery:       draft.gallery       ? { ...base.gallery, ...draft.gallery } : base.gallery,
    ourStory:      draft.ourStory      ? { ...(base.ourStory ?? { blocks: [] }), ...draft.ourStory } : base.ourStory,
    labelOverrides: draft.labelOverrides ?? base.labelOverrides,
  };
}

type EditorTab = 'settings' | 'theme' | 'palette' | 'customize' | 'labels' | 'intro' | 'hero' | 'details' | 'schedule' | 'gallery' | 'ourStory' | 'rsvp' | 'faq' | 'gifts' | 'music' | 'nearby' | null;
type PreviewDevice = 'mobile' | 'tablet' | 'desktop';

const DRAFT_STORAGE_KEY = 'invitely_draft_invite';
const DRAFT_IMAGES_KEY = 'invitely_draft_images';

interface InviteBuilderContextType {
  // Invite data
  invite: Invite | null;
  title: string;
  setTitle: (title: string) => void;
  inviteType: InviteType;
  setInviteType: (type: InviteType) => void;
  eventType: EventType;
  setEventType: (type: EventType) => void;
  eventDate: string | null;
  setEventDate: (date: string | null) => void;
  content: InviteContent;
  theme: InviteTheme;
  sectionsEnabled: InviteSectionsEnabled;
  sectionTagRules: SectionTagRules;
  sectionOrder: string[];
  images: InviteImage[];

  // Editor state
  activeTab: EditorTab;
  setActiveTab: (tab: EditorTab) => void;
  previewDevice: PreviewDevice;
  setPreviewDevice: (device: PreviewDevice) => void;
  previewKey: number;
  isReady: boolean;
  refreshPreview: () => void;
  activeLocale: string;
  setActiveLocale: (locale: string) => void;

  // Save state
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;

  // Linked monogram
  monogramSvg: string | null;
  setMonogramSvg: (svg: string | null) => void;

  // Actions
  setInvite: (invite: Invite) => void;
  setInviteSlug: (slug: string) => void;
  updateContent: <K extends keyof InviteContent>(section: K, valueOrUpdater: InviteContent[K] | ((prev: InviteContent[K]) => InviteContent[K])) => void;
  updateSectionField: <K extends keyof InviteContent>(section: K, field: string, value: any) => void;
  updateNestedField: <K extends keyof InviteContent>(section: K, nestedKey: string, field: string, value: any) => void;
  updateTheme: (updates: Partial<InviteTheme>) => void;
  toggleSection: (section: keyof InviteSectionsEnabled) => void;
  setSectionTagRule: (section: keyof InviteSectionsEnabled, tags: string[]) => void;
  setSectionOrder: (order: string[]) => void;
  save: () => Promise<void>;
  addImage: (image: InviteImage) => void;
  removeImage: (imageId: number) => void;

  updateLabelOverrides: (overrides: InviteLabelOverrides) => void;

  // Location helpers
  addLocation: () => void;
  removeLocation: (index: number) => void;
  updateLocationField: (index: number, field: string, value: any) => void;
}

const InviteBuilderContext = createContext<InviteBuilderContextType | null>(null);

const AUTO_SAVE_DELAY = 800; // ms — fast enough to feel instant, slow enough to avoid per-keystroke saves

export function InviteBuilderProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const updateInvite = useUpdateInvite();
  const upsertTranslation = useUpsertTranslation();

  // Core state
  const [invite, setInviteState] = useState<Invite | null>(null);
  const [content, setContent] = useState<InviteContent>({
    hero: { title: '', subtitle: '', dateDisplay: '', backgroundImageId: null },
    details: { locations: [{ id: 'loc-1', label: 'Ceremony', name: '', address: '', showMap: false, coordinates: null, sameAsFirst: false }], dressCode: '', additionalInfo: '', contactEmail: '', contactPhone: '' },
    schedule: { items: [] },
    gallery: { imageIds: [] },
    ourStory: { blocks: [] },
    rsvp: { collectEmail: true, collectDietary: false, allowPlusOnes: false, maxPlusOnes: 1, allowMaybe: true, customMessage: '', hideFoodForTags: [] },
    faq: { items: [] },
    gifts: { title: '', description: '', registryUrl: '', registryLabel: '' },
    nearby: { items: [] },
  });
  const [theme, setTheme] = useState<InviteTheme>({
    preset: 'elegant',
    colors: { background: '#ffffff', accent: '#c9a959', headingText: '#1a1a2e', bodyText: '#1a1a2e', nav: '#1a1a2e', button: '#1a1a2e' },
    fonts: { heading: 'Playfair Display', body: 'Inter' },
  });
  const [sectionsEnabled, setSectionsEnabled] = useState<InviteSectionsEnabled>({
    hero: true, details: true, schedule: true, gallery: true, ourStory: false, rsvp: true, faq: false, gifts: false, nearby: false,
  });
  const [sectionTagRules, setSectionTagRulesState] = useState<SectionTagRules>({});
  const [sectionOrder, setSectionOrderState] = useState<string[]>(['hero', 'details', 'schedule', 'rsvp', 'gallery', 'ourStory', 'faq', 'gifts', 'nearby']);
  const [images, setImages] = useState<InviteImage[]>([]);
  const [title, setTitleState] = useState('');
  const [inviteType, setInviteTypeState] = useState<InviteType>('invite');
  const [eventType, setEventTypeState] = useState<EventType>('wedding');
  const [eventDate, setEventDateState] = useState<string | null>(null);

  // Editor state
  const [activeTab, setActiveTab] = useState<EditorTab>(null);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('mobile');
  const [previewKey, setPreviewKey] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [activeLocale, setActiveLocaleState] = useState('en');

  // Translation draft — holds edits for the active non-English locale
  const [translationDraft, setTranslationDraft] = useState<InviteTranslatableContent>({});
  const [isTranslationDirty, setIsTranslationDirty] = useState(false);
  const translationSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setActiveLocale = useCallback((locale: string) => {
    if (!SUPPORTED_LOCALES.find(l => l.code === locale)) return;
    setActiveLocaleState(locale);
  }, []);

  const refreshPreview = useCallback(() => {
    setPreviewKey(k => k + 1);
  }, []);

  const [monogramSvg, setMonogramSvgState] = useState<string | null>(null);

  // Save state
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Always-current ref so save() never reads stale closure content
  const contentRef = useRef(content);
  useEffect(() => { contentRef.current = content; }, [content]);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Auto-save timer ref
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setInviteSlug = useCallback((slug: string) => {
    setInviteState(prev => prev ? { ...prev, slug } : prev);
  }, []);

  // Set invite and initialize state
  const setInvite = useCallback((newInvite: Invite) => {
    setInviteState(newInvite);
    setTitleState(newInvite.title || '');
    setInviteTypeState(newInvite.invite_type || 'invite');
    setEventTypeState(newInvite.event_type || 'wedding');
    setEventDateState(newInvite.event_date || null);
    setTheme(newInvite.theme);
    setMonogramSvgState(newInvite.monogram_svg ?? null);
    setSectionsEnabled({ ...newInvite.sections_enabled, nearby: newInvite.sections_enabled?.nearby ?? false, ourStory: newInvite.sections_enabled?.ourStory ?? false });
    setSectionTagRulesState(newInvite.section_tag_rules ?? {});
    const savedOrder = newInvite.section_order ?? ['hero', 'details', 'schedule', 'rsvp', 'gallery', 'faq', 'gifts'];
    const withNearby = savedOrder.includes('nearby') ? savedOrder : [...savedOrder, 'nearby'];
    setSectionOrderState(withNearby.includes('ourStory') ? withNearby : [...withNearby, 'ourStory']);
    setIsReady(true);

    const apiImages = newInvite.images || [];
    let contentToUse = { ...newInvite.content };

    // Auto-migrate old location (singular) → locations (array) format
    if (contentToUse.details && 'location' in contentToUse.details && !contentToUse.details.locations) {
      const oldLoc = (contentToUse.details as any).location;
      contentToUse = {
        ...contentToUse,
        details: {
          ...contentToUse.details,
          locations: [{
            id: 'loc-1',
            label: 'Venue',
            name: oldLoc?.name || '',
            address: oldLoc?.address || '',
            showMap: oldLoc?.showMap || false,
            coordinates: oldLoc?.coordinates || null,
            sameAsFirst: false,
          }],
        },
      };
      delete (contentToUse.details as any).location;
    }

    // Migrate eveningArrivalTime → last location's arrivalTime
    if ((contentToUse.details as any).eveningArrivalTime) {
      const locs = contentToUse.details.locations || [];
      if (locs.length > 0) {
        const updatedLocs = [...locs];
        const lastIdx = updatedLocs.length - 1;
        if (!updatedLocs[lastIdx].arrivalTime) {
          updatedLocs[lastIdx] = { ...updatedLocs[lastIdx], arrivalTime: (contentToUse.details as any).eveningArrivalTime };
        }
        contentToUse = { ...contentToUse, details: { ...contentToUse.details, locations: updatedLocs } };
      }
      const { eveningArrivalTime: _, ...detailsWithout } = contentToUse.details as any;
      contentToUse = { ...contentToUse, details: detailsWithout };
    }
    if (!contentToUse.rsvp.hideFoodForTags) {
      contentToUse = { ...contentToUse, rsvp: { ...contentToUse.rsvp, hideFoodForTags: [] } };
    }
    if (!contentToUse.ourStory) {
      contentToUse = { ...contentToUse, ourStory: { blocks: [] } };
    }

    // Check sessionStorage for placeholder images from draft import
    const draftImagesJson = sessionStorage.getItem(DRAFT_IMAGES_KEY);
    let draftImages: InviteImage[] = [];
    if (draftImagesJson) {
      try {
        draftImages = JSON.parse(draftImagesJson);
      } catch { /* ignore */ }
      sessionStorage.removeItem(DRAFT_IMAGES_KEY);
    }

    // Auto-populate: if gallery is enabled but imageIds is empty and there are
    // no real uploaded images, restore placeholder images (covers invites that
    // were imported before the fix and had their IDs stripped).
    const galleryIds = contentToUse.gallery?.imageIds || [];
    const hasRealImages = apiImages.some(img => img.section === 'gallery');

    if (newInvite.sections_enabled.gallery && galleryIds.length === 0 && !hasRealImages && draftImages.length === 0) {
      // Restore default placeholder IDs
      contentToUse = {
        ...contentToUse,
        gallery: { ...contentToUse.gallery, imageIds: PLACEHOLDER_IMAGES.map(img => img.id) },
      };
    }

    // Ensure any server-side gallery images are in imageIds (handles race condition
    // where image upload refetch arrives before content auto-save completes)
    const apiGalleryIds = apiImages
      .filter(img => img.section === 'gallery')
      .sort((a, b) => a.order - b.order)
      .map(img => img.id);
    const galleryIdSet = new Set(contentToUse.gallery?.imageIds || []);
    const missingIds = apiGalleryIds.filter(id => !galleryIdSet.has(id));
    if (missingIds.length > 0) {
      contentToUse = {
        ...contentToUse,
        gallery: { ...contentToUse.gallery, imageIds: [...(contentToUse.gallery?.imageIds || []), ...missingIds] },
      };
    }

    setContent(contentToUse);

    // Resolve placeholder image objects for any negative IDs in gallery.imageIds
    const resolvedGalleryIds = contentToUse.gallery?.imageIds || [];
    const placeholderImgs = resolvePlaceholderImages(resolvedGalleryIds);

    // Merge: API images + draft images + resolved placeholders (deduplicated)
    const allExtra = [...draftImages, ...placeholderImgs];
    const existingIds = new Set(apiImages.map(img => img.id));
    const mergedImages = [...apiImages, ...allExtra.filter(img => !existingIds.has(img.id))];
    setImages(mergedImages);

    setIsDirty(false);
    setLastSavedAt(new Date(newInvite.updated_at));
  }, []);

  // Set title
  const setTitle = useCallback((newTitle: string) => {
    setTitleState(newTitle);
    setIsDirty(true);
  }, []);

  // Set invite type
  const setInviteType = useCallback((type: InviteType) => {
    setInviteTypeState(type);
    setIsDirty(true);
  }, []);

  // Set event type
  const setEventType = useCallback((type: EventType) => {
    setEventTypeState(type);
    setIsDirty(true);
  }, []);

  // Set event date — also updates content.hero.dateDisplay so the hero text stays in sync
  const setEventDate = useCallback((date: string | null) => {
    setEventDateState(date);
    setIsDirty(true);
    setContent((prev) => {
      const fmt = ((prev.hero as any).dateFormat as DateFormat) || 'full';
      return {
        ...prev,
        hero: {
          ...prev.hero,
          dateDisplay: date ? formatEventDate(date, fmt) : '',
        },
      };
    });
  }, []);

  // Update content for a specific section (accepts value or updater function)
  const updateContent = useCallback(<K extends keyof InviteContent>(
    section: K,
    valueOrUpdater: InviteContent[K] | ((prev: InviteContent[K]) => InviteContent[K])
  ) => {
    if (activeLocale !== 'en' && TRANSLATABLE_SECTIONS.has(section as string)) {
      // Write to translation draft — fall back to base content if draft section is empty
      setTranslationDraft((prev) => ({
        ...prev,
        [section]: typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as any)(prev[section as keyof InviteTranslatableContent] ?? content[section])
          : valueOrUpdater,
      }));
      setIsTranslationDirty(true);
    } else {
      setContent((prev) => ({
        ...prev,
        [section]: typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: InviteContent[K]) => InviteContent[K])(prev[section])
          : valueOrUpdater,
      }));
      setIsDirty(true);
    }
  }, [activeLocale, content]);

  // Update a single field within a section (uses functional update to avoid stale closures)
  const updateSectionField = useCallback(<K extends keyof InviteContent>(section: K, field: string, value: any) => {
    const sectionStr = section as string;
    if (activeLocale !== 'en' && TRANSLATABLE_FIELDS[sectionStr]?.has(field)) {
      // Write translatable field to draft
      setTranslationDraft((prev) => ({
        ...prev,
        [sectionStr]: { ...(prev[sectionStr as keyof InviteTranslatableContent] ?? {}), [field]: value },
      }));
      setIsTranslationDirty(true);
    } else {
      // Non-translatable field (or English) — always writes to main content
      setContent((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
      setIsDirty(true);
    }
  }, [activeLocale]);

  // Update a nested field (e.g., details.location.name)
  const updateNestedField = useCallback(<K extends keyof InviteContent>(
    section: K,
    nestedKey: string,
    field: string,
    value: any
  ) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedKey]: { ...(prev[section] as any)[nestedKey], [field]: value },
      },
    }));
    setIsDirty(true);
  }, []);

  // Update theme
  const updateTheme = useCallback((updates: Partial<InviteTheme>) => {
    setTheme((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  // Toggle section visibility
  const toggleSection = useCallback((section: keyof InviteSectionsEnabled) => {
    setSectionsEnabled((prev) => ({ ...prev, [section]: !prev[section] }));
    setIsDirty(true);
  }, []);

  // Set tag-based visibility rule for a section
  const setSectionTagRule = useCallback((section: keyof InviteSectionsEnabled, tags: string[]) => {
    setSectionTagRulesState((prev) => ({ ...prev, [section]: tags }));
    setIsDirty(true);
  }, []);

  // Reorder sections
  const setSectionOrder = useCallback((order: string[]) => {
    setSectionOrderState(order);
    setIsDirty(true);
  }, []);

  // Add image to local state
  const addImage = useCallback((image: InviteImage) => {
    setImages((prev) => [...prev, image]);
    // If it's a gallery image, update gallery content
    if (image.section === 'gallery') {
      setContent((prev) => ({
        ...prev,
        gallery: { ...prev.gallery, imageIds: [...prev.gallery.imageIds, image.id] },
      }));
      setIsDirty(true);
    }
  }, []);

  // Remove image from local state
  const removeImage = useCallback((imageId: number) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
    // Remove from gallery content if applicable
    setContent((prev) => ({
      ...prev,
      gallery: { ...prev.gallery, imageIds: prev.gallery.imageIds.filter((id) => id !== imageId) },
      hero: prev.hero.backgroundImageId === imageId ? { ...prev.hero, backgroundImageId: null } : prev.hero,
    }));
    setIsDirty(true);
  }, []);

  // Location helpers
  const addLocation = useCallback(() => {
    setContent((prev) => {
      const locations = prev.details.locations || [];
      const suggestedLabels = ['Reception', 'After-party', 'Venue'];
      const usedLabels = new Set(locations.map(l => l.label));
      const label = suggestedLabels.find(l => !usedLabels.has(l)) || `Location ${locations.length + 1}`;
      const newLocation: InviteLocationItem = {
        id: `loc-${Date.now()}`,
        label,
        name: label,
        address: '',
        showMap: false,
        coordinates: null,
        sameAsFirst: false,
      };
      return {
        ...prev,
        details: { ...prev.details, locations: [...locations, newLocation] },
      };
    });
    setIsDirty(true);
    setPreviewKey(k => k + 1);
  }, []);

  const removeLocation = useCallback((index: number) => {
    setContent((prev) => {
      const locations = [...(prev.details.locations || [])];
      locations.splice(index, 1);
      return {
        ...prev,
        details: { ...prev.details, locations },
      };
    });
    setIsDirty(true);
  }, []);

  const updateLocationField = useCallback((index: number, field: string, value: any) => {
    setContent((prev) => {
      const locations = [...(prev.details.locations || [])];
      locations[index] = { ...locations[index], [field]: value };
      return {
        ...prev,
        details: { ...prev.details, locations },
      };
    });
    setIsDirty(true);
  }, []);

  const updateLabelOverrides = useCallback((overrides: InviteLabelOverrides) => {
    if (activeLocale !== 'en') {
      setTranslationDraft((prev) => ({ ...prev, labelOverrides: overrides }));
      setIsTranslationDirty(true);
    } else {
      setContent((prev) => ({ ...prev, labelOverrides: overrides }));
      setIsDirty(true);
    }
  }, [activeLocale]);

  const setMonogramSvg = useCallback((svg: string | null) => {
    setMonogramSvgState(svg);
    setIsDirty(true);
  }, []);

  // Save function
  const save = useCallback(async () => {
    if (!invite || !isDirty) return;

    setIsSaving(true);
    try {
      await updateInvite.mutateAsync({
        inviteId: invite.id,
        data: { title, invite_type: inviteType, event_type: eventType, content: contentRef.current, theme, sections_enabled: sectionsEnabled, section_tag_rules: sectionTagRules, section_order: sectionOrder, event_date: eventDate, monogram_svg: monogramSvg },
      });
      setIsDirty(false);
      setLastSavedAt(new Date());
      // Notify any open preview tabs that new data is available
      try {
        const bc = new BroadcastChannel('invite-saved');
        bc.postMessage({ inviteId: invite.id });
        bc.close();
      } catch { /* BroadcastChannel not supported */ }
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to save', 'error');
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [invite, isDirty, title, inviteType, eventType, content, theme, sectionsEnabled, sectionOrder, eventDate, monogramSvg, updateInvite, showToast]);

  // Auto-save effect - only when we have a real invite (not test mode)
  useEffect(() => {
    if (!isDirty || !invite?.id) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      save().catch(() => {
        // Error already handled in save function
      });
    }, AUTO_SAVE_DELAY);

    // Cleanup on unmount or dependency change
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDirty, save, invite?.id]);

  // Initialise translation draft when locale or invite changes
  useEffect(() => {
    if (activeLocale === 'en') {
      setTranslationDraft({});
      setIsTranslationDirty(false);
      return;
    }
    const existing = invite?.translations?.find(t => t.locale === activeLocale)?.content ?? {};
    setTranslationDraft(existing);
    setIsTranslationDirty(false);
  }, [activeLocale, invite?.id]);

  // Auto-save translation draft
  useEffect(() => {
    if (!isTranslationDirty || !invite?.id || activeLocale === 'en') return;
    if (translationSaveTimerRef.current) clearTimeout(translationSaveTimerRef.current);
    const savedDraft = translationDraft;
    const savedLocale = activeLocale;
    translationSaveTimerRef.current = setTimeout(() => {
      upsertTranslation.mutate(
        { inviteId: invite.id, locale: savedLocale, content: savedDraft },
        {
          onSuccess: () => {
            setIsTranslationDirty(false);
            // Patch local invite.translations so locale-switch re-init sees fresh data
            setInviteState((prev) => {
              if (!prev) return prev;
              const existing = prev.translations ?? [];
              const idx = existing.findIndex(t => t.locale === savedLocale);
              const updated = [...existing];
              if (idx >= 0) {
                updated[idx] = { locale: savedLocale, content: savedDraft };
              } else {
                updated.push({ locale: savedLocale, content: savedDraft });
              }
              return { ...prev, translations: updated };
            });
          },
        }
      );
    }, AUTO_SAVE_DELAY);
    return () => {
      if (translationSaveTimerRef.current) clearTimeout(translationSaveTimerRef.current);
    };
  }, [isTranslationDirty, invite?.id, activeLocale, translationDraft, upsertTranslation]);

  // localStorage persistence for draft invites (id: 0)
  useEffect(() => {
    // Only save to localStorage for draft invites
    if (!invite || invite.id !== 0) return;

    const draftData = {
      title,
      inviteType,
      eventDate,
      content,
      theme,
      sectionsEnabled,
      images,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
  }, [invite, title, inviteType, eventDate, content, theme, sectionsEnabled, images]);

  // Expose merged content: when translating, overlay draft text over English base
  const displayContent = activeLocale !== 'en'
    ? mergeDraftIntoContent(content, translationDraft)
    : content;

  const value: InviteBuilderContextType = {
    invite,
    title,
    setTitle,
    inviteType,
    setInviteType,
    eventType,
    setEventType,
    eventDate,
    setEventDate,
    content: displayContent,
    theme,
    sectionsEnabled,
    sectionTagRules,
    sectionOrder,
    images,
    activeTab,
    setActiveTab,
    previewDevice,
    setPreviewDevice,
    previewKey,
    isReady,
    refreshPreview,
    activeLocale,
    setActiveLocale,
    isDirty,
    isSaving,
    lastSavedAt,
    monogramSvg,
    setMonogramSvg,
    setInvite,
    setInviteSlug,
    updateContent,
    updateSectionField,
    updateNestedField,
    updateTheme,
    toggleSection,
    setSectionTagRule,
    setSectionOrder,
    save,
    addImage,
    removeImage,
    updateLabelOverrides,
    addLocation,
    removeLocation,
    updateLocationField,
  };

  return (
    <InviteBuilderContext.Provider value={value}>
      {children}
    </InviteBuilderContext.Provider>
  );
}

export function useInviteBuilder() {
  const context = useContext(InviteBuilderContext);
  if (!context) {
    throw new Error('useInviteBuilder must be used within InviteBuilderProvider');
  }
  return context;
}
